import type { ChangeEvent, FocusEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import validate from 'validate.js';
import { TotpService } from 'api';
import {
  CONFIRMATION_BUTTON_BACK,
  CONFIRMATION_BUTTON_NEXT,
  INVALID_OTP_ERROR,
  KEY_COPIED,
  MESSAGE_BOX_TITLE_SUCCESS,
  MISSING_BACKUP_CODE,
  MISSING_RECOVERY_CODE,
  OTP_RECOVERY_GENERIC_VERIFY_ERROR,
  OTP_RECOVERY_REQUEST_GENERIC_ERROR,
  OTP_TOO_MANY_RECOVERY_REQUESTS_MESSAGE,
  RESULT_SUCCESS,
  SUCCESS_INSIDE,
} from 'common/constants/auth';
import { ERROR_OCCURRED } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import type { AuthenticationInputFormState } from 'pages/Auth/components/AuthenticationInput';
import type { BackupCodesCloseHandler } from 'pages/Auth/components/BackupCodes';
import { UNKNOWN_OTP_ERROR_MESSAGE } from 'pages/Auth/components/OTP/OTP.constants';
import type { OTPProps } from 'pages/Auth/components/OTP/OTP.props';
import type { MessageBoxState, OTPResponse } from 'pages/Auth/components/OTP/OTP.types';
import {
  getOtpUriParams,
  normalizeBackupCodeValue,
  normalizeOtpError,
  normalizeOtpValue,
  normalizeRecoveryCodeValue,
} from 'pages/Auth/components/OTP/OTP.utils';
import { constraints, constraintsBkCode, constraintsRecoveryCode } from 'pages/Auth/components/OTP/OTP.validation';
import { useAuthResendCooldown, useLogOutUser } from 'pages/Auth/hooks';
import {
  clearPendingBackupCodes,
  clearRememberedTwoFactor,
  getPendingBackupCodes,
  setOtpVerified,
  setPendingBackupCodes,
  setRememberedTwoFactor,
  setStoredAccessToken,
  setStoredRefreshToken,
} from 'pages/Auth/utils';
import { useResponse } from 'services/hooks/useResponse';
import { Logger } from 'utilities';

type OtpFlowStep = 'result' | 'setup' | 'verify' | null;
type RecoveryStage = 'idle' | 'request' | 'sent';

interface UseOtpFlowResult {
  backupCodes: string[];
  currentStep: OtpFlowStep;
  formState: AuthenticationInputFormState;
  hasBackupCodes: boolean;
  handleCancelSetup: () => void;
  handleBackupCodesAcknowledge: BackupCodesCloseHandler;
  handleCopyToClipboard: () => Promise<void>;
  handleInputBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  handleToggleOtpKey: () => void;
  isCodeWorking: boolean;
  isCompleting: boolean;
  isGeneratingBackupCodes: boolean;
  isRecoveryResendCoolingDown: boolean;
  isRequestingRecoveryCode: boolean;
  isSubmitting: boolean;
  messageBoxData: MessageBoxState;
  otpData?: OTPResponse;
  otpSecretKey?: string;
  recoveryRequestError: string | null;
  recoveryResendRemainingSeconds: number;
  recoveryStage: RecoveryStage;
  rememberThisComputer: boolean;
  showRememberThisComputer: boolean;
  showOtpKey: boolean;
  handleRecoveryBackToBackupCode: () => void;
  handleRequestRecoveryCode: () => Promise<void>;
  startBackupCodeFlow: () => void;
  startRecoveryRequestFlow: () => void;
  username?: string;
  generateBackupCodes: () => Promise<boolean>;
  logOutUser: () => void;
  returnToOtpVerificationFlow: () => void;
  setRememberThisComputer: (remember: boolean) => void;
}

const createDefaultMessageBoxState = (
  onBack: () => void,
  onNext: (() => void) | undefined
): MessageBoxState => ({
  show: false,
  title: MESSAGE_BOX_TITLE_SUCCESS,
  result: RESULT_SUCCESS,
  backBtnText: CONFIRMATION_BUTTON_BACK,
  nextBtnText: CONFIRMATION_BUTTON_NEXT,
  type: SUCCESS_INSIDE,
  success: true,
  actionBack: onBack,
  actionNext: () => onNext?.(),
});

const initialFormState: AuthenticationInputFormState = {
  dbErrors: {},
  errors: {},
  isValid: false,
  touched: {},
  values: {},
};

const useOtpFlow = ({
  setDeviceData,
  setDisplayReauthenticate,
  showActionButtons = true,
  userHas2FA,
  verifyOtp,
}: OTPProps): UseOtpFlowResult => {
  const [{ user }] = useStore() as UseStoreValues;
  const logOutUser = useLogOutUser();
  const { successNotification } = useResponse();
  const [formState, setFormState] = useState<AuthenticationInputFormState>(initialFormState);
  const defaultMessageBoxState = useMemo(
    () => createDefaultMessageBoxState(logOutUser, () => verifyOtp?.(true, 'verified')),
    [logOutUser, verifyOtp]
  );
  // Backup codes are only shown once, so keep them refresh-safe until the user acknowledges them.
  const [restoredPendingBackupCodes] = useState(() => getPendingBackupCodes());

  const [otpData, setOtpData] = useState<OTPResponse>();
  const [otpSecretKey, setOtpSecretKey] = useState<string>();
  const [showOtpKey, setShowOtpKey] = useState(false);
  const [rememberThisComputer, setRememberThisComputer] = useState(false);
  const [messageBoxData, setMessageBoxData] = useState<MessageBoxState>(defaultMessageBoxState);
  const [isCodeWorking, setIsCodeWorking] = useState(false);
  const [showBackupCodesStep, setShowBackupCodesStep] = useState(() => Boolean(restoredPendingBackupCodes?.length));
  const [backupCodes, setBackupCodes] = useState<string[]>(() => restoredPendingBackupCodes ?? []);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldCompleteAfterBackupCodesClose, setShouldCompleteAfterBackupCodesClose] = useState(
    () => Boolean(restoredPendingBackupCodes?.length)
  );
  const [recoveryStage, setRecoveryStage] = useState<RecoveryStage>('idle');
  const [recoveryRequestError, setRecoveryRequestError] = useState<string | null>(null);
  const [isRequestingRecoveryCode, setIsRequestingRecoveryCode] = useState(false);
  // Recovery wipes the user's 2FA devices server-side; this locally overrides `userHas2FA` so the
  // hook immediately treats the account as unenrolled and routes into forced re-enrollment, without
  // waiting on the parent to re-render with a fresh prop value.
  const [hasCompletedRecovery, setHasCompletedRecovery] = useState(false);
  const {
    isCoolingDown: isRecoveryResendCoolingDown,
    remainingSeconds: recoveryResendRemainingSeconds,
    startCooldown: startRecoveryResendCooldown,
  } = useAuthResendCooldown();
  const effectiveUserHas2FA = userHas2FA && !hasCompletedRecovery;
  const showRememberThisComputer = effectiveUserHas2FA;

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    let nextValue = event.target.value;

    if (event.target.name === 'otp') {
      nextValue = normalizeOtpValue(event.target.value);
    } else if (event.target.name === 'backupCode') {
      nextValue = normalizeBackupCodeValue(event.target.value);
    } else if (event.target.name === 'recoveryCode') {
      nextValue = normalizeRecoveryCodeValue(event.target.value);
    }

    setFormState(currentFormState => ({
      ...currentFormState,
      dbErrors: {},
      values: {
        [event.target.name]: nextValue,
      },
    }));
  }, []);

  const handleInputBlur = useCallback<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(event => {
    const { name } = event.target;

    setFormState(currentFormState => ({
      ...currentFormState,
      touched: {
        ...currentFormState.touched,
        [name]: true,
      },
    }));
  }, []);

  const completeVerifiedOtp = useCallback(() => {
    if (verifyOtp) {
      setIsCompleting(true);
    }

    if (effectiveUserHas2FA && !isCodeWorking && user?.id && showRememberThisComputer) {
      if (rememberThisComputer) {
        setRememberedTwoFactor(user.id);
      } else {
        clearRememberedTwoFactor(user.id);
      }
    }

    verifyOtp?.(true, 'verified');
    setOtpVerified();
  }, [effectiveUserHas2FA, isCodeWorking, rememberThisComputer, showRememberThisComputer, user?.id, verifyOtp]);

  const verifyBackupCode = useCallback(async () => {
    const { backupCode } = formState.values;
    if (!backupCode) {
      setFormState(prevFormState => ({
        ...prevFormState,
        errors: {
          backupCode: MISSING_BACKUP_CODE,
        },
      }));
      return;
    }

    try {
      const response = await TotpService.apiTotpStaticVerifyCreate(backupCode as string);
      if (response) {
        completeVerifiedOtp();
      }
    } catch (error: unknown) {
      const errorMsg = normalizeOtpError(error, ERROR_OCCURRED);
      setFormState(prevFormState => ({
        ...prevFormState,
        dbErrors: {
          backupCode: [errorMsg],
        },
      }));
    }
  }, [completeVerifiedOtp, formState.values]);

  const startRecoveryRequestFlow = useCallback(() => {
    setFormState(initialFormState);
    setRecoveryRequestError(null);
    setRecoveryStage('request');
  }, []);

  const handleRecoveryBackToBackupCode = useCallback(() => {
    setFormState(initialFormState);
    setRecoveryRequestError(null);
    setRecoveryStage('idle');
  }, []);

  const handleRequestRecoveryCode = useCallback(async () => {
    setIsRequestingRecoveryCode(true);
    setRecoveryRequestError(null);

    try {
      await TotpService.apiTotpRecoveryRequestCreate();
      setFormState(initialFormState);
      setRecoveryStage('sent');
      startRecoveryResendCooldown();
    } catch (error: unknown) {
      setRecoveryRequestError(
        normalizeOtpError(error, OTP_RECOVERY_REQUEST_GENERIC_ERROR, OTP_TOO_MANY_RECOVERY_REQUESTS_MESSAGE)
      );
    } finally {
      setIsRequestingRecoveryCode(false);
    }
  }, [startRecoveryResendCooldown]);

  const handleRecoveryCodeSubmit = useCallback(async () => {
    const { recoveryCode } = formState.values;
    if (!recoveryCode) {
      setFormState(prevFormState => ({
        ...prevFormState,
        errors: {
          recoveryCode: MISSING_RECOVERY_CODE,
        },
      }));
      return;
    }

    try {
      const response = await TotpService.apiTotpRecoveryVerifyCreate({ code: recoveryCode as string });
      setStoredAccessToken(response.token.access);
      setStoredRefreshToken(response.token.refresh);
      setHasCompletedRecovery(true);
      setRecoveryStage('idle');
      setIsCodeWorking(false);
      setFormState(initialFormState);
    } catch (error: unknown) {
      const errorMsg = normalizeOtpError(error, OTP_RECOVERY_GENERIC_VERIFY_ERROR);
      setFormState(prevFormState => ({
        ...prevFormState,
        dbErrors: {
          recoveryCode: [errorMsg],
        },
      }));
    }
  }, [formState.values]);

  const generateBackupCodes = useCallback(async () => {
    setIsGeneratingBackupCodes(true);

    try {
      const response = await TotpService.apiTotpStaticCreateCreate();

      if (response) {
        setBackupCodes(response.tokens);
        setPendingBackupCodes(response.tokens);
        setShowBackupCodesStep(true);
        return true;
      }
    } catch (error: unknown) {
      // A 403 means this sensitive backup-code action needs a fresh user reauthentication.
      if (isAxiosError(error) && error.response?.status === 403 && setDisplayReauthenticate) {
        setDisplayReauthenticate(true);
      }
    } finally {
      setIsGeneratingBackupCodes(false);
    }

    return false;
  }, [setDisplayReauthenticate]);

  const validateOtp = useCallback(
    async ({ showMessageBox }: { showMessageBox: boolean }) => {
      try {
        const response = await TotpService.apiTotpVerifyCreate(formState.values.otp as string);

        if (response?.verified) {
          if (setDeviceData) {
            setDeviceData(prevDeviceData => ({
              ...prevDeviceData,
              confirmed: true,
            }));
          }

          const shouldGenerateBackupCodes = !effectiveUserHas2FA || !user?.has_tokens_2fa;
          if (shouldGenerateBackupCodes) {
            // Keep the OTP step mounted until the new backup codes are ready so setup does not flash
            // an intermediate success surface before we can hand off to the codes screen.
            const openedBackupCodesStep = await generateBackupCodes();

            if (openedBackupCodesStep) {
              if (showMessageBox) {
                setMessageBoxData({
                  ...defaultMessageBoxState,
                  show: true,
                });
              } else {
                setShouldCompleteAfterBackupCodesClose(true);
              }
              return;
            }
          }

          setMessageBoxData({
            ...defaultMessageBoxState,
            show: showMessageBox,
          });

          if (!showMessageBox) {
            completeVerifiedOtp();
          }
        } else {
          setFormState(prevFormState => ({
            ...prevFormState,
            dbErrors: {
              otp: [INVALID_OTP_ERROR],
            },
          }));
        }
      } catch (error: unknown) {
        const errorMsg = normalizeOtpError(error, ERROR_OCCURRED);
        setFormState(prevFormState => ({
          ...prevFormState,
          dbErrors: {
            otp: [errorMsg],
          },
        }));
      }
    },
    [
      defaultMessageBoxState,
      effectiveUserHas2FA,
      formState.values.otp,
      generateBackupCodes,
      completeVerifiedOtp,
      setDeviceData,
      user?.has_tokens_2fa,
    ]
  );

  useEffect(() => {
    let isMounted = true;

    const generateOrReturnUserTOTP = async () => {
      try {
        const response = await TotpService.apiTotpCreateList();
        if (!isMounted) {
          return;
        }
        setOtpData(response);
        const otpUriParams = getOtpUriParams(response.url);
        setOtpSecretKey(otpUriParams?.secret ?? '');
      } catch (error) {
        Logger.error('useOtpFlow.generateOrReturnUserTOTP', error instanceof Error ? error : UNKNOWN_OTP_ERROR_MESSAGE);
      }
    };

    if (!effectiveUserHas2FA) {
      generateOrReturnUserTOTP();
    }

    return () => {
      isMounted = false;
    };
  }, [effectiveUserHas2FA]);

  useEffect(() => {
    if (otpData && setDeviceData) {
      const { persistent_id: persistentId } = otpData;
      setDeviceData(prevDeviceData => ({
        ...prevDeviceData,
        persistent_id: persistentId,
      }));
    }
  }, [otpData, setDeviceData]);

  useEffect(() => {
    let activeConstraints: typeof constraints | typeof constraintsBkCode | typeof constraintsRecoveryCode = constraints;
    if (isCodeWorking && recoveryStage === 'sent') {
      activeConstraints = constraintsRecoveryCode;
    } else if (isCodeWorking) {
      activeConstraints = constraintsBkCode;
    }

    const errors = validate(formState.values, activeConstraints);
    setFormState(currentFormState => ({
      ...currentFormState,
      errors: errors ?? {},
      isValid: !errors,
    }));
  }, [formState.values, isCodeWorking, recoveryStage]);

  const handleBackupCodesAcknowledge: BackupCodesCloseHandler = useCallback(() => {
    const shouldKeepBackupCodesUntilAuthRouteExit = showActionButtons && verifyOtp;
    const completeAfterBackupCodes = () => {
      if (!shouldKeepBackupCodesUntilAuthRouteExit) {
        clearPendingBackupCodes();
      }

      completeVerifiedOtp();
    };

    if (shouldCompleteAfterBackupCodesClose) {
      setShouldCompleteAfterBackupCodesClose(false);
      completeAfterBackupCodes();
      return;
    }

    // The sign-in setup flow should continue immediately after the user acknowledges
    // their new backup codes, instead of dropping them back into OTP verification again.
    if (messageBoxData.show && verifyOtp) {
      completeAfterBackupCodes();
      return;
    }

    clearPendingBackupCodes();
    setShowBackupCodesStep(false);
  }, [completeVerifiedOtp, messageBoxData.show, shouldCompleteAfterBackupCodesClose, showActionButtons, verifyOtp]);

  const handleToggleOtpKey = useCallback(() => {
    setShowOtpKey(currentValue => !currentValue);
  }, []);

  const handleCopyToClipboard = useCallback(async () => {
    try {
      if (otpSecretKey) {
        await navigator.clipboard.writeText(otpSecretKey);
        successNotification(KEY_COPIED);
      }
    } catch (error) {
      Logger.error('useOtpFlow.handleCopyToClipboard', error instanceof Error ? error : UNKNOWN_OTP_ERROR_MESSAGE);
    }
  }, [otpSecretKey, successNotification]);

  const handleCancelSetup = useCallback(() => {
    clearPendingBackupCodes();
    logOutUser();
  }, [logOutUser]);

  const returnToOtpVerificationFlow = useCallback(() => {
    setIsCodeWorking(false);
    setFormState(initialFormState);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isCodeWorking && recoveryStage === 'sent') {
        await handleRecoveryCodeSubmit();
        return;
      }

      if (!effectiveUserHas2FA) {
        await validateOtp({ showMessageBox: true });
        return;
      }

      if (!isCodeWorking) {
        await validateOtp({ showMessageBox: false });
        return;
      }

      await verifyBackupCode();
    } finally {
      setIsSubmitting(false);
    }
  }, [effectiveUserHas2FA, handleRecoveryCodeSubmit, isCodeWorking, isSubmitting, recoveryStage, validateOtp, verifyBackupCode]);

  const currentStep = useMemo<OtpFlowStep>(() => {
    // Freshly generated backup codes must stay on the success step until the user closes that handoff,
    // even if the surrounding auth shell already recognizes the device as configured.
    if (showBackupCodesStep || messageBoxData.show) {
      return 'result';
    }

    if (effectiveUserHas2FA) {
      return 'verify';
    }

    if (otpData) {
      return 'setup';
    }

    return null;
  }, [effectiveUserHas2FA, messageBoxData.show, otpData, showBackupCodesStep]);

  const startBackupCodeFlow = useCallback(() => {
    setFormState(initialFormState);
    setIsCodeWorking(true);
  }, []);

  return {
    backupCodes,
    currentStep,
    formState,
    generateBackupCodes,
    hasBackupCodes: Boolean(user?.has_tokens_2fa) || backupCodes.length > 0,
    handleCancelSetup,
    handleBackupCodesAcknowledge,
    handleCopyToClipboard,
    handleInputBlur,
    handleInputChange,
    handleRecoveryBackToBackupCode,
    handleRequestRecoveryCode,
    handleSubmit,
    handleToggleOtpKey,
    isCodeWorking,
    isCompleting,
    isGeneratingBackupCodes,
    isRecoveryResendCoolingDown,
    isRequestingRecoveryCode,
    isSubmitting,
    logOutUser,
    messageBoxData,
    otpData,
    otpSecretKey,
    recoveryRequestError,
    recoveryResendRemainingSeconds,
    recoveryStage,
    rememberThisComputer,
    returnToOtpVerificationFlow,
    setRememberThisComputer,
    showRememberThisComputer,
    showOtpKey,
    startBackupCodeFlow,
    startRecoveryRequestFlow,
    username: user?.email,
  };
};

export default useOtpFlow;
